import { useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";

type Values = { email: string; password: string };
type Errors = Partial<Record<keyof Values, string>>;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function useLoginForm(opts: { onSuccess: () => void }) {
  const { signIn } = useAuth();
  const [values, setValues] = useState<Values>({ email: "", password: "" });
  const [errors, setErrors] = useState<Errors>({});
  const [submittedOnce, setSubmittedOnce] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const canSubmit = useMemo(() => {
    return EMAIL_REGEX.test(values.email.trim()) && values.password.length >= 6;
  }, [values]);

  function validate(next: Values): Errors {
    const e: Errors = {};
    const email = next.email.trim();

    if (!email) e.email = "Email is required.";
    else if (!EMAIL_REGEX.test(email)) e.email = "Enter a valid email address.";

    if (!next.password) e.password = "Password is required.";
    else if (next.password.length < 6) e.password = "Password must be at least 6 characters.";

    return e;
  }

  function setField<K extends keyof Values>(key: K, value: Values[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));

    if (submittedOnce) {
      const next = { ...values, [key]: value } as Values;
      setErrors(validate(next));
    }
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmittedOnce(true);
    setFormError(null);

    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      setIsLoading(true);
      await signIn(values.email.trim(), values.password);
      opts.onSuccess();
    } catch (err: any) {
      // Firebase error codes
      let message = "Login failed. Please try again.";
      
      if (err.code === "auth/invalid-credential" || err.code === "auth/wrong-password") {
        message = "Invalid email or password.";
      } else if (err.code === "auth/user-not-found") {
        message = "No account found with this email.";
      } else if (err.code === "auth/too-many-requests") {
        message = "Too many failed attempts. Please try again later.";
      } else if (err.code === "auth/network-request-failed") {
        message = "Network error. Check your connection.";
      }
      
      setFormError(message);
    } finally {
      setIsLoading(false);
    }
  }

  return {
    values,
    errors,
    submittedOnce,
    canSubmit,
    setField,
    onSubmit,
    isLoading,
    formError,
  };
}